// MARK:- OS 2.0 web component: hover-syncs items by index across multiple lists.
//
// Used by sections where two (or more) lists need to highlight the same-position
// item simultaneously on hover (e.g. featured-recipe pairs a text list with an
// image list, hovering either side highlights both).
//
// File: standalone hand-edited asset, NOT a build output. Sections include via:
//
//   <script src="{{ 'component-connected-lists.js' | asset_url }}" type="module"></script>
//
// Markup contract (data-as-* prefixed to avoid jQuery collisions):
//
//   <as-connected-lists>
//     <ol data-as-hover-list>
//       <li data-as-hover-item>…</li>
//       <li data-as-hover-item>…</li>
//     </ol>
//     <ol data-as-hover-list>
//       <li data-as-hover-item>…</li>
//       <li data-as-hover-item>…</li>
//     </ol>
//   </as-connected-lists>
//
// Behavior (mirrors master's `hoverList` in src/js/theme.js):
// - Hovering an item adds `is-active` to that item AND the same-index item in
//   every sibling list. All other items get `is-inactive`.
// - Mouseleave (off the whole component) resets every item to `is-active`,
//   matching master's "rest state is all-active" pattern.
// - No-op on touch devices (no hover events fire). Items stay in their default
//   no-class state, which the CSS treats as the resting visual.

class AsConnectedLists extends HTMLElement {
  connectedCallback() {
    AsConnectedLists.ensureBaseStyle();
    this.bindEvents();
  }

  static ensureBaseStyle() {
    if (document.querySelector('style[data-as-connected-lists-base]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-as-connected-lists-base', '');
    style.textContent = 'as-connected-lists { display: block; }';
    document.head.appendChild(style);
  }

  bindEvents() {
    this.addEventListener('mouseover', (event) => {
      const item = event.target.closest('[data-as-hover-item]');
      if (!item || !this.contains(item)) return;
      if (event.relatedTarget && item.contains(event.relatedTarget)) return;

      const list = item.closest('[data-as-hover-list]');
      if (!list) return;

      const itemsInThisList = list.querySelectorAll('[data-as-hover-item]');
      const index = Array.from(itemsInThisList).indexOf(item);
      if (index === -1) return;

      this.activateIndex(index);
    });

    this.addEventListener('mouseleave', () => this.resetAll());
  }

  activateIndex(index) {
    this.querySelectorAll('[data-as-hover-list]').forEach((list) => {
      const items = list.querySelectorAll('[data-as-hover-item]');
      items.forEach((item, i) => {
        if (i === index) {
          item.classList.add('is-active');
          item.classList.remove('is-inactive');
        } else {
          item.classList.remove('is-active');
          item.classList.add('is-inactive');
        }
      });
    });
  }

  resetAll() {
    this.querySelectorAll('[data-as-hover-list]').forEach((list) => {
      list.querySelectorAll('[data-as-hover-item]').forEach((item) => {
        item.classList.add('is-active');
        item.classList.remove('is-inactive');
      });
    });
  }
}

if (!customElements.get('as-connected-lists')) {
  customElements.define('as-connected-lists', AsConnectedLists);
}
